// Renders a single state's shape as a centered SVG.
// Filters out tiny subpath artifacts (the simplified Albers paths leave
// degenerate slivers in some states like Rhode Island) so the displayed
// outline reads as one clean shape.

import { useMemo } from 'react';

function bbox(d) {
  const numbers = d.match(/-?\d+(?:\.\d+)?/g);
  if (!numbers) return { x: 0, y: 0, w: 100, h: 100, area: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < numbers.length - 1; i += 2) {
    const x = parseFloat(numbers[i]);
    const y = parseFloat(numbers[i + 1]);
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const w = Math.max(0, maxX - minX);
  const h = Math.max(0, maxY - minY);
  return { x: minX, y: minY, w, h, area: w * h };
}

// Split the path into subpaths (each starting with M / m), drop fragments
// whose bounding-box area is < 3% of the largest subpath. Keeps Hawaii
// (multi-island) intact while culling Rhode Island's stray slivers.
function cleanPath(d) {
  const subpaths = d.split(/(?=[Mm])/).filter(s => s.trim().length > 1);
  if (subpaths.length <= 1) return d;
  const measured = subpaths.map(sp => ({ sp, area: bbox(sp).area }));
  const maxArea = Math.max(...measured.map(m => m.area));
  if (maxArea <= 0) return d;
  const threshold = maxArea * 0.03;
  const kept = measured.filter(m => m.area >= threshold).map(m => m.sp);
  return kept.join('');
}

export default function StateShape({ state, size = 260 }) {
  const path = useMemo(() => cleanPath(state.path), [state.path]);
  const box = useMemo(() => bbox(path), [path]);
  const pad = Math.max(box.w, box.h) * 0.04;
  const viewBox = `${box.x - pad} ${box.y - pad} ${box.w + pad * 2} ${box.h + pad * 2}`;
  const aspect = box.w / box.h;
  const width = aspect >= 1 ? size : size * aspect;
  const height = aspect >= 1 ? size / aspect : size;

  return (
    <svg
      className="shape-svg"
      viewBox={viewBox}
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Outline of ${state.name}`}
    >
      <path className="shape-fill" d={path} />
    </svg>
  );
}
